import { Head, Link, router } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { ChevronLeft, ChevronRight } from "lucide-react"

type ListingRef = { id: number; month_number: number; day_number: number; month_name: string } | null
type Listing    = { id: number; month_number: number; day_number: number; month_name: string }

type BandRef = { name: string; slug: string | null }

type PersonRow = {
  id: number; name: string; slug: string
  date: string | null; precision: string | null
  bands: BandRef[]
}

type Act = {
  id: number; name: string | null; slug: string | null; type: string; notes: string | null
}

type GigShow = {
  gig_date: string | null; year: string
  venue_name: string | null; venue_slug: string | null
  venue_state: string | null; venue_region_name: string | null
  acts: Act[]
}

type RegionOption = { id: number; name: string }

type RecordingRow = {
  id: number; title: string; slug: string
  first_charted: string | null; highest_position: number | null
  recordable_name: string | null; recordable_slug: string | null; recordable_type: string | null
}

type ChartRow = {
  id: number; chart_date: string | null; position: number
  recording_title: string | null; recording_slug: string | null
  recordable_name: string | null; recordable_slug: string | null; recordable_type: string | null
}

type Props = {
  listing: Listing
  birthdays: PersonRow[]
  deaths: PersonRow[]
  gigs: GigShow[]
  gig_states: string[]
  gig_regions: RegionOption[]
  gig_state: string | null
  gig_region_id: number | null
  first_charted: RecordingRow[]
  chart_listings: ChartRow[]
  prev_week_chart_listings: ChartRow[]
  prev_listing: ListingRef
  next_listing: ListingRef
}

function SectionHeader({ title, count }: { title: string; count: number }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <h2 className="text-base font-semibold text-stone-900 dark:text-white">{title}</h2>
      {count > 0 && (
        <span className="text-xs bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 px-2 py-0.5 rounded-full font-medium">
          {count}
        </span>
      )}
    </div>
  )
}

function EmptyRow({ label }: { label: string }) {
  return <p className="text-sm text-stone-400 dark:text-stone-600 italic">{label}</p>
}

function yearFromDate(iso: string | null) {
  return iso ? iso.slice(0, 4) : "?"
}

function personYear(iso: string | null, precision: string | null) {
  if (precision === "day_month") return "?"
  return yearFromDate(iso)
}

function recordableLink(name: string | null, slug: string | null, type: string | null) {
  if (!name) return null
  const path = type === "Band" ? `/bands/${slug}` : `/people/${slug}`
  return <Link href={path} className="hover:underline text-amber-600 dark:text-amber-400">{name}</Link>
}

function ChartTable({ rows, emptyLabel }: { rows: ChartRow[]; emptyLabel: string }) {
  if (rows.length === 0) return <EmptyRow label={emptyLabel} />
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-xs text-stone-400 dark:text-stone-500 border-b border-stone-100 dark:border-stone-800">
          <th className="pb-1.5 pr-3 w-12">#</th>
          <th className="pb-1.5 pr-3">Recording</th>
          <th className="pb-1.5 pr-3">Artist</th>
          <th className="pb-1.5">Year</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-stone-50 dark:divide-stone-800/50">
        {rows.map((cl) => (
          <tr key={cl.id}>
            <td className="py-1.5 pr-3 font-mono text-stone-500 dark:text-stone-400">{cl.position}</td>
            <td className="py-1.5 pr-3">
              {cl.recording_slug
                ? <Link href={`/recordings/${cl.recording_slug}`} className="hover:underline text-stone-800 dark:text-stone-200">{cl.recording_title}</Link>
                : <span className="text-stone-700 dark:text-stone-300">{cl.recording_title}</span>
              }
            </td>
            <td className="py-1.5 pr-3 text-stone-600 dark:text-stone-400">
              {recordableLink(cl.recordable_name, cl.recordable_slug, cl.recordable_type)}
            </td>
            <td className="py-1.5 text-stone-400 dark:text-stone-500">{yearFromDate(cl.chart_date)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function prevWeekLabel(listing: Listing) {
  const d = new Date(2004, listing.month_number - 1, listing.day_number)
  d.setDate(d.getDate() - 7)
  const month = d.toLocaleString("en-AU", { month: "long" })
  return `${month} ${d.getDate()} — previous week's chart`
}

function GigFilter({
  listing, states, regions, currentState, currentRegionId,
}: {
  listing: Listing
  states: string[]
  regions: RegionOption[]
  currentState: string | null
  currentRegionId: number | null
}) {
  if (states.length === 0 && regions.length === 0) return null

  function navigate(state: string | null, regionId: number | null) {
    const params: Record<string, string> = {}
    if (state) params.gig_state = state
    if (regionId) params.gig_region_id = String(regionId)
    router.get(`/admin/daily_listings/${listing.id}`, params, { preserveState: false })
  }

  return (
    <div className="flex flex-wrap items-center gap-3 mb-4">
      {states.length > 0 && (
        <select
          value={currentState ?? ""}
          onChange={(e) => navigate(e.target.value || null, currentRegionId)}
          className="text-sm border border-stone-200 dark:border-stone-700 rounded-md px-2 py-1 bg-white dark:bg-stone-900 text-stone-700 dark:text-stone-300"
        >
          <option value="">All states</option>
          {states.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      )}
      {regions.length > 0 && (
        <select
          value={currentRegionId ?? ""}
          onChange={(e) => navigate(currentState, e.target.value ? Number(e.target.value) : null)}
          className="text-sm border border-stone-200 dark:border-stone-700 rounded-md px-2 py-1 bg-white dark:bg-stone-900 text-stone-700 dark:text-stone-300"
        >
          <option value="">All regions</option>
          {regions.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
      )}
      {(currentState || currentRegionId) && (
        <button
          onClick={() => navigate(null, null)}
          className="text-xs text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 underline"
        >
          Clear filter
        </button>
      )}
    </div>
  )
}

export default function DailyListingsShow({
  listing, birthdays, deaths, gigs,
  gig_states, gig_regions, gig_state, gig_region_id,
  first_charted, chart_listings, prev_week_chart_listings, prev_listing, next_listing
}: Props) {
  const title = `${listing.month_name} ${listing.day_number} — Daily Listing`

  return (
    <>
      <Head title={`${title} — Admin`}>
        <meta name="robots" content="noindex" />
      </Head>
      <AdminShell>
        <h1 className="mb-6">{title}</h1>
        {/* Navigation */}
        <div className="flex items-center justify-between mb-8">
          {prev_listing
            ? <Link href={`/admin/daily_listings/${prev_listing.id}`} className="flex items-center gap-1 text-sm text-stone-500 hover:text-amber-600 dark:hover:text-amber-400 no-underline transition-colors">
                <ChevronLeft className="w-4 h-4" />
                {prev_listing.month_name} {prev_listing.day_number}
              </Link>
            : <span />
          }
          <Link href="/admin/daily_listings" className="text-xs text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 no-underline transition-colors">
            All days
          </Link>
          {next_listing
            ? <Link href={`/admin/daily_listings/${next_listing.id}`} className="flex items-center gap-1 text-sm text-stone-500 hover:text-amber-600 dark:hover:text-amber-400 no-underline transition-colors">
                {next_listing.month_name} {next_listing.day_number}
                <ChevronRight className="w-4 h-4" />
              </Link>
            : <span />
          }
        </div>

        <div className="space-y-10">
          {/* Birthdays */}
          <section>
            <SectionHeader title="Birthdays" count={birthdays.length} />
            {birthdays.length === 0
              ? <EmptyRow label="No birthdays recorded." />
              : <ul className="space-y-1">
                  {birthdays.map((p) => (
                    <li key={p.id} className="flex items-baseline gap-2 text-sm">
                      <span className="text-stone-400 dark:text-stone-500 w-10 shrink-0">{personYear(p.date, p.precision)}</span>
                      <span>
                        <Link href={`/people/${p.slug}`} className="text-stone-800 dark:text-stone-200 hover:underline">{p.name}</Link>
                        {p.bands.length > 0 && (
                          <span className="text-stone-400 dark:text-stone-500 text-xs ml-1">
                            ({p.bands.map((b, i) => (
                              <span key={b.slug ?? b.name}>
                                {i > 0 && ", "}
                                {b.slug
                                  ? <Link href={`/bands/${b.slug}`} className="hover:underline">{b.name}</Link>
                                  : b.name
                                }
                              </span>
                            ))})
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
            }
          </section>

          {/* Deaths */}
          <section>
            <SectionHeader title="Deaths" count={deaths.length} />
            {deaths.length === 0
              ? <EmptyRow label="No deaths recorded." />
              : <ul className="space-y-1">
                  {deaths.map((p) => (
                    <li key={p.id} className="flex items-baseline gap-2 text-sm">
                      <span className="text-stone-400 dark:text-stone-500 w-10 shrink-0">{personYear(p.date, p.precision)}</span>
                      <span>
                        <Link href={`/people/${p.slug}`} className="text-stone-800 dark:text-stone-200 hover:underline">{p.name}</Link>
                        {p.bands.length > 0 && (
                          <span className="text-stone-400 dark:text-stone-500 text-xs ml-1">
                            ({p.bands.map((b, i) => (
                              <span key={b.slug ?? b.name}>
                                {i > 0 && ", "}
                                {b.slug
                                  ? <Link href={`/bands/${b.slug}`} className="hover:underline">{b.name}</Link>
                                  : b.name
                                }
                              </span>
                            ))})
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
            }
          </section>

          {/* Gigs */}
          <section>
            <SectionHeader title="Gigs" count={gigs.length} />
            <GigFilter
              listing={listing}
              states={gig_states}
              regions={gig_regions}
              currentState={gig_state}
              currentRegionId={gig_region_id}
            />
            {gigs.length === 0
              ? <EmptyRow label="No gigs recorded." />
              : <ul className="space-y-3">
                  {gigs.map((show, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <span className="text-stone-400 dark:text-stone-500 w-10 shrink-0 pt-px">{show.year}</span>
                      <div>
                        {/* Acts — comma-separated on one line */}
                        <span className="font-medium text-stone-800 dark:text-stone-200">
                          {show.acts.map((act, ai) => (
                            <span key={act.id}>
                              {ai > 0 && <span className="text-stone-400 dark:text-stone-500">, </span>}
                              {act.slug
                                ? <Link href={`/${act.type === "Band" ? "bands" : "people"}/${act.slug}`} className="hover:underline">{act.name}</Link>
                                : act.name
                              }
                            </span>
                          ))}
                        </span>
                        {/* Venue */}
                        {show.venue_name && (
                          <span className="text-stone-500 dark:text-stone-400">
                            {" @ "}
                            {show.venue_slug
                              ? <Link href={`/venues/${show.venue_slug}`} className="hover:underline">{show.venue_name}</Link>
                              : show.venue_name
                            }
                            {show.venue_state && (
                              <span className="text-xs text-stone-400 dark:text-stone-500 ml-1">({show.venue_state})</span>
                            )}
                          </span>
                        )}
                        {/* Per-act notes */}
                        {show.acts.some((a) => a.notes) && (
                          <div className="mt-0.5 space-y-0.5">
                            {show.acts.filter((a) => a.notes).map((a) => (
                              <p key={a.id} className="text-xs text-stone-400 dark:text-stone-500">{a.name}: {a.notes}</p>
                            ))}
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
            }
          </section>

          {/* First charted */}
          <section>
            <SectionHeader title="First charted" count={first_charted.length} />
            {first_charted.length === 0
              ? <EmptyRow label="No chart entries recorded." />
              : <ul className="space-y-1">
                  {first_charted.map((r) => (
                    <li key={r.id} className="flex items-center gap-2 text-sm">
                      <span className="text-stone-400 dark:text-stone-500 w-10 shrink-0">{yearFromDate(r.first_charted)}</span>
                      <Link href={`/recordings/${r.slug}`} className="text-stone-800 dark:text-stone-200 hover:underline">{r.title}</Link>
                      {r.recordable_name && (
                        <span className="text-stone-500 dark:text-stone-400 text-xs">
                          — {recordableLink(r.recordable_name, r.recordable_slug, r.recordable_type)}
                        </span>
                      )}
                      {r.highest_position && (
                        <span className="text-xs text-stone-400 dark:text-stone-500">#{r.highest_position}</span>
                      )}
                    </li>
                  ))}
                </ul>
            }
          </section>

          {/* Chart listings — this day */}
          <section>
            <SectionHeader title={`${listing.month_name} ${listing.day_number} — chart positions`} count={chart_listings.length} />
            <ChartTable rows={chart_listings} emptyLabel="No chart listings recorded for this day." />
          </section>

          {/* Chart listings — previous week */}
          <section>
            <SectionHeader title={prevWeekLabel(listing)} count={prev_week_chart_listings.length} />
            <ChartTable rows={prev_week_chart_listings} emptyLabel="No chart listings recorded for the previous week." />
          </section>
        </div>
      </AdminShell>
    </>
  )
}
